/**
 * Chatter Panel — one chatter's identity and activity histogram (R18c)
 *
 * Opened by clicking a speaker's name in the live chat log. That is the whole
 * navigation story for now, and it is deliberate: `ChatMessage.chatterId`
 * already carries the Twitch user id, so this needs no new backend at all,
 * whereas a browsable chatter index needs a paged list endpoint that does not
 * exist yet (tracked separately as R18f).
 *
 * WHAT THE GRID IS. R18b accumulates a 7x24 message-count histogram per
 * chatter: a flat array of 168 ints, `index = weekday * 24 + hour`, Monday = 0.
 * Stored buckets are UTC and stay UTC. The server rotates them into the
 * channel's local hours on every read and sends BOTH arrays; this component
 * renders `activity_local` and never does the conversion itself. Two reasons
 * that matters: the conversion has a DST approximation that belongs in one
 * documented place, and the label-minute arithmetic depends on floor division,
 * which JavaScript's `%` does not do for negative offsets.
 *
 * THE HALF-HOUR THING IS NOT AN EDGE CASE. Australia/Adelaide is UTC+9:30, so
 * every column here is a :30-to-:30 window. `bucket_label_minutes` carries that
 * remainder and the axis states it. No count is ever split across two columns
 * to make the labels rounder — that would turn an integer histogram into floats
 * and flatten real peaks to hide an offset the label can simply tell you.
 *
 * NULL IS NOT ZERO, AND THEY MUST NOT LOOK ALIKE. `activity_local` is null
 * until a chatter has been counted AND flushed. A null renders as an explicit
 * "no data" state with no grid; a real array of 168 zeros renders as a grid
 * that is empty AND says so. That distinction is the only thing that
 * distinguishes a working write-behind flush from a silent one, so collapsing
 * them into one blank rectangle would destroy the diagnostic.
 *
 * A LOAD FAILURE HERE IS AN INLINE BANNER, NOT A TOAST. Same rule as the
 * channel page: a surface that has no content to show has nothing to toast
 * over, and the message must persist rather than time out. Toasts stay for
 * mutations.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiErrorMessage } from '@/api/client'
import { getChatter, type ChatterDetail } from '@/api/chattersApi'
import { Pill } from '@/components/BotStatus'
import { AlertCircle, Clock, Loader2, X } from 'lucide-react'
import clsx from 'clsx'

/**
 * Re-exported so nothing that already imports `ChatterDetail` from this
 * component breaks. The type's HOME is `@/api/chattersApi` (R18f) — it
 * describes a wire shape, not this panel, and `ChannelChatters` needs it
 * without pulling in a modal.
 */
export type { ChatterDetail } from '@/api/chattersApi'

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const HOURS = 24

/** "UTC+9:30", "UTC-5", "UTC" — for showing what offset was actually applied. */
function formatOffset(minutes: number): string {
  if (minutes === 0) return 'UTC'
  const sign = minutes < 0 ? '-' : '+'
  const abs = Math.abs(minutes)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  return m === 0 ? `UTC${sign}${h}` : `UTC${sign}${h}:${String(m).padStart(2, '0')}`
}

/** Column label: the START of the window this column covers. */
function hourLabel(hour: number, labelMinutes: number): string {
  return `${String(hour).padStart(2, '0')}:${String(labelMinutes).padStart(2, '0')}`
}

function formatWhen(iso: string | null): string {
  if (!iso) return 'never'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? 'unknown' : d.toLocaleString()
}

/**
 * Five intensity steps. Relative to the chatter's own busiest hour, not to any
 * global scale: the question is "when is THIS person around", so a quiet
 * regular's shape should read as clearly as a firehose's.
 */
function intensityClass(count: number, max: number): string {
  if (count <= 0) return 'bg-surface-overlay'
  const ratio = count / max
  if (ratio > 0.75) return 'bg-synth-cyan'
  if (ratio > 0.5) return 'bg-synth-cyan/70'
  if (ratio > 0.25) return 'bg-synth-cyan/45'
  return 'bg-synth-cyan/25'
}

export default function ChatterPanel({
  channelId,
  twitchUserId,
  fallbackName,
  onClose,
}: {
  channelId: number
  twitchUserId: string
  /** Name from the chat line, shown while the row loads and if it has none. */
  fallbackName: string
  onClose: () => void
}) {
  const [chatter, setChatter] = useState<ChatterDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      setIsLoading(true)
      try {
        const data = await getChatter(channelId, twitchUserId)
        if (cancelled) return
        setChatter(data)
        setLoadError(null)
      } catch (err) {
        if (cancelled) return
        // A 404 here is ordinary, not broken: the row is created by the bot on
        // a chatter's first line, and this API never creates one. It can happen
        // for a line replayed from the durable window whose chatter row was
        // removed by a channel-level cascade.
        setLoadError(apiErrorMessage(err, 'Could not load this chatter'))
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [channelId, twitchUserId])

  // Escape closes, matching every other dismissable surface in the app.
  const onKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose],
  )
  useEffect(() => {
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onKey])

  const buckets = chatter?.activity_local ?? null

  const { max, total, peakIndex } = useMemo(() => {
    if (!buckets) return { max: 0, total: 0, peakIndex: -1 }
    let max = 0
    let total = 0
    let peakIndex = -1
    buckets.forEach((v, i) => {
      total += v
      if (v > max) {
        max = v
        peakIndex = i
      }
    })
    return { max, total, peakIndex }
  }, [buckets])

  const title = chatter?.display_name || chatter?.login || fallbackName
  const labelMinutes = chatter?.bucket_label_minutes ?? 0

  return (
    <div
      className="fixed inset-0 z-[55] flex items-center justify-center p-4 bg-black/60"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="card w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Chatter details for ${title}`}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
            {chatter?.login && chatter.login !== title.toLowerCase() && (
              <p className="text-text-muted text-xs mt-0.5">{chatter.login}</p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close chatter details"
            className="shrink-0 p-1 rounded text-text-muted hover:text-text-primary"
          >
            <X size={18} />
          </button>
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 text-text-secondary py-8 justify-center">
            <Loader2 size={18} className="animate-spin" />
            Loading chatter…
          </div>
        )}

        {!isLoading && loadError && (
          <div className="flex items-start gap-2 p-3 rounded-lg border border-state-danger/40 bg-state-danger-bg text-sm">
            <AlertCircle size={18} className="shrink-0 mt-px text-state-danger" />
            <span className="text-text-primary">{loadError}</span>
          </div>
        )}

        {!isLoading && !loadError && chatter && (
          <>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5 text-sm">
              <div>
                <dt className="text-text-secondary text-xs mb-0.5">First seen</dt>
                <dd className="text-text-primary">{formatWhen(chatter.first_seen_at)}</dd>
              </div>
              <div>
                <dt className="text-text-secondary text-xs mb-0.5">Last seen</dt>
                <dd className="text-text-primary">{formatWhen(chatter.last_seen_at)}</dd>
              </div>
              <div>
                <dt className="text-text-secondary text-xs mb-0.5">Last greeted</dt>
                <dd className="text-text-primary">{formatWhen(chatter.last_greeted_at)}</dd>
              </div>
            </dl>

            <div className="flex items-center gap-2 flex-wrap mb-3">
              <h3 className="text-sm font-semibold text-text-primary flex items-center gap-1.5">
                <Clock size={15} />
                When they chat
              </h3>
              {chatter.timezone_is_set ? (
                <Pill tone="cyan">
                  {chatter.timezone} · {formatOffset(chatter.utc_offset_minutes)}
                </Pill>
              ) : (
                <Pill tone="warning">UTC — channel timezone not set</Pill>
              )}
            </div>

            {!chatter.timezone_is_set && (
              <p className="text-text-muted text-xs mb-3 leading-relaxed">
                Hours below are UTC. Set this channel's timezone in Settings to read
                them as local hours — that changes only what's drawn here, never the
                stored counts.
              </p>
            )}

            {buckets === null ? (
              /* Fork F: never counted. NOT a grid of zeros — see the file header. */
              <div className="p-4 rounded-lg bg-surface-raised/50 text-sm">
                <p className="text-text-primary">No activity recorded yet.</p>
                <p className="text-text-muted text-xs mt-1.5 leading-relaxed">
                  This chatter has not been counted and flushed since the activity
                  histogram was deployed. Counts are written behind the chat path
                  and appear within about ten seconds of someone speaking — an
                  empty state that persists after they have chatted means the
                  counter is not running, not that they are quiet.
                </p>
              </div>
            ) : (
              <>
                {total === 0 && (
                  <div className="mb-3 p-3 rounded-lg border border-state-warning/40 bg-state-warning-bg text-xs leading-relaxed text-text-primary">
                    All 168 buckets are present but every one is zero. That is a
                    flush that ran and counted nothing — distinct from "never
                    counted", and worth investigating rather than reading as a
                    quiet week.
                  </div>
                )}

                <div className="overflow-x-auto">
                  <div className="inline-block min-w-full">
                    {/* Hour axis, labelled every three columns. With a sub-hour
                        offset these read :30 or :45, because that is genuinely
                        where each column's window starts. */}
                    <div
                      className="grid gap-0.5 mb-1 ml-9"
                      style={{ gridTemplateColumns: `repeat(${HOURS}, minmax(0, 1fr))` }}
                    >
                      {Array.from({ length: HOURS }, (_, h) => (
                        <div
                          key={h}
                          className="text-[9px] text-text-muted text-left whitespace-nowrap"
                        >
                          {h % 3 === 0 ? hourLabel(h, labelMinutes) : ''}
                        </div>
                      ))}
                    </div>

                    {DAYS.map((day, d) => (
                      <div key={day} className="flex items-center gap-0.5 mb-0.5">
                        <div className="w-8 shrink-0 text-[10px] text-text-secondary">
                          {day}
                        </div>
                        <div
                          className="grid gap-0.5 flex-1"
                          style={{ gridTemplateColumns: `repeat(${HOURS}, minmax(0, 1fr))` }}
                        >
                          {Array.from({ length: HOURS }, (_, h) => {
                            const index = d * HOURS + h
                            const count = buckets[index] ?? 0
                            const from = hourLabel(h, labelMinutes)
                            const to = hourLabel((h + 1) % HOURS, labelMinutes)
                            return (
                              <div
                                key={h}
                                title={`${day} ${from}–${to} — ${count} message${count === 1 ? '' : 's'}`}
                                className={clsx(
                                  'h-4 rounded-sm',
                                  intensityClass(count, max),
                                )}
                              />
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-text-muted text-xs mt-3 leading-relaxed">
                  {total.toLocaleString()} message{total === 1 ? '' : 's'} counted
                  {peakIndex >= 0 && max > 0 && (
                    <>
                      {' '}· busiest {DAYS[Math.floor(peakIndex / HOURS)]}{' '}
                      {hourLabel(peakIndex % HOURS, labelMinutes)}–
                      {hourLabel(((peakIndex % HOURS) + 1) % HOURS, labelMinutes)}
                    </>
                  )}
                  . Buckets are stored in UTC and converted on every read using this
                  channel's offset as at now, so counts recorded under a different
                  daylight-saving regime can sit one hour out. The stored data is
                  unaffected.
                </p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
